import { API_HEAD, BASE_URL } from '@/lib/apiConfig';

type AdminRef = {
  id?: string | number;
  fullName?: string;
  email?: string;
};

export interface Product {
  id: string;
  title: string;
  category: 'Công Cụ Đám Mây' | 'Phát Triển' | 'Phân Tích' | 'Bảo Mật' | 'Marketing' | 'Kỹ thuật' | 'Khác';
  description: string;
  overview?: string;
  icon?: string;
  iconColor?: string;
  features?: string[];
  dashboardImage?: string;
  videoFile?: string;
  zipFile?: string;
  guideFile?: string;
  status: 'active' | 'inactive';
  sortOrder?: number;
  views: number;
  downloads: number;
  rating: number | string;
  comments: number;
  createdBy?: number | AdminRef;
  updatedBy?: number | AdminRef;
  deletedBy?: number | AdminRef;
  productCreator?: AdminRef;
  productUpdater?: AdminRef;
  productDeleter?: AdminRef;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  isDeleted: boolean;
}

export interface GetProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: number;
  isDeleted?: boolean;
}

export interface ProductStats {
  overview: {
    totalProducts: number;
    activeProducts: number;
    inactiveProducts: number;
    deletedProducts: number;
    totalViews: number;
    totalDownloads: number;
    avgRating: number;
  };
  categoryStats: Array<{
    _id: string;
    count: number;
  }>;
}

export interface CreateProductData {
  title: string;
  category: string;
  description: string;
  overview?: string;
  icon?: string;
  iconColor?: string;
  features?: string[];
  dashboardImage?: string;
  videoFile?: string;
  zipFile?: string;
  guideFile?: string;
  status?: 'active' | 'inactive';
  sortOrder?: number;
}

export interface UpdateProductData extends Partial<CreateProductData> {}

export interface BulkActionData {
  action: 'delete' | 'restore' | 'permanentDelete' | 'toggleStatus';
  productIds: string[];
  status?: 'active' | 'inactive';
}

class ProductService {
  // Public request method - không yêu cầu authentication (dùng cho trang public)
  private async publicRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_HEAD}products${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Lỗi không xác định');
    }
  }

  // Authenticated request method - yêu cầu authentication (dùng cho admin)
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { makeAuthenticatedRequest } = await import('./apiHelper');
    
    const url = `${API_HEAD}products${endpoint}`;
    const baseUrl = BASE_URL;
    
    try {
      const response = await makeAuthenticatedRequest<T>(url, options, baseUrl);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 401) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('adminToken');
          }
          throw new Error(errorData.message || 'Không có token xác thực. Vui lòng đăng nhập lại.');
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Lỗi không xác định');
    }
  }

  // Public method - không yêu cầu đăng nhập
  async getProducts(params: GetProductsParams = {}) {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `?${queryString}` : '';
    
    return this.publicRequest<{
      success: boolean;
      data: {
        products: Product[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalProducts: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
    }>(endpoint);
  }

  async getProductsForAdmin(params: GetProductsParams = {}) {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/admin/all${queryString ? `?${queryString}` : ''}`;
    
    return this.request<{
      success: boolean;
      data: {
        products: Product[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalProducts: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
    }>(endpoint);
  }

  // Public method - không yêu cầu đăng nhập
  async getProductById(id: string) {
    return this.publicRequest<{
      success: boolean;
      data: Product;
    }>(`/${id}`);
  }

  async getProductByIdForAdmin(id: string) {
    return this.request<{
      success: boolean;
      data: Product;
    }>(`/admin/${id}`);
  }

  async createProduct(productData: CreateProductData) {
    return this.request<{
      success: boolean;
      message: string;
      data: Product;
    }>('', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(id: string, productData: UpdateProductData) {
    return this.request<{
      success: boolean;
      message: string;
      data: Product;
    }>(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(id: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>(`/${id}`, {
      method: 'DELETE',
    });
  }

  async permanentDeleteProduct(id: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>(`/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  async restoreProduct(id: string) {
    return this.request<{
      success: boolean;
      message: string;
      data: Product;
    }>(`/${id}/restore`, {
      method: 'POST',
    });
  }

  async toggleProductStatus(id: string, status: 'active' | 'inactive') {
    return this.request<{
      success: boolean;
      message: string;
      data: Product;
    }>(`/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async updateProductSortOrder(id: string, sortOrder: number) {
    return this.request<{
      success: boolean;
      message: string;
      data: Product;
    }>(`/${id}/sort`, {
      method: 'PATCH',
      body: JSON.stringify({ sortOrder }),
    });
  }

  async getTrashProducts(params: GetProductsParams = {}) {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/admin/trash${queryString ? `?${queryString}` : ''}`;
    
    return this.request<{
      success: boolean;
      data: {
        products: Product[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalProducts: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
    }>(endpoint);
  }

  async bulkAction(actionData: BulkActionData) {
    return this.request<{
      success: boolean;
      message: string;
      data: { modifiedCount: number };
    }>('/bulk', {
      method: 'POST',
      body: JSON.stringify(actionData),
    });
  }

  async getProductStats() {
    return this.request<{
      success: boolean;
      data: ProductStats;
    }>('/stats');
  }

  async getProductCategories() {
    return this.request<{
      success: boolean;
      data: string[];
      message: string;
    }>('/categories');
  }

  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_HEAD}products/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async uploadVideo(file: File) {
    const formData = new FormData();
    formData.append('video', file);

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_HEAD}products/upload-video`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async uploadZip(file: File) {
    const formData = new FormData();
    formData.append('zip', file);

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_HEAD}products/upload-zip`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Xử lý lỗi 413 với thông báo chi tiết
      if (response.status === 413) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        throw new Error(
          `Lỗi 413: Kích thước file quá lớn!\n\n` +
          `Kích thước file: ${fileSizeMB} MB\n` +
          `Giới hạn: 2GB (2048 MB)\n\n` +
          `Vui lòng kiểm tra:\n` +
          `1. File phải nhỏ hơn 2GB\n` +
          `2. Nginx config cần có: client_max_body_size 2048m;\n` +
          `3. Reload Nginx: sudo nginx -t && sudo systemctl reload nginx`
        );
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async uploadGuide(file: File) {
    const formData = new FormData();
    formData.append('guide', file);

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_HEAD}products/upload-guide`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

const productService = new ProductService();
export default productService;
